import { Component } from "react"
import PropTypes from "prop-types"
import PeriodSelector from "components/period-selector"
import StackedLineChart from "components/charts/stacked-line-chart"
import RangeLineChart from "components/charts/range-line-chart"
import { formatLongDate } from "helpers/formatters/dates"
import { makeCurrencyFormatter } from "helpers/formatters/numbers"
import { DATE, PERIOD } from "helpers/shapes"
import RiskDetails from "modules/risk-details"
import { RISK_TREND, RISK_CATEGORY } from "./shapes"
import Tooltip from "./components/tooltip"
import NoData from "components/no-data"
import SelectedDate from "components/selected-date"
import "./style.scss"
import { Card, useTheme } from "@mui/material"
import { alpha } from "@mui/material/styles"

function GmvRangeLineChart(props) {
  const theme = useTheme()
  const color = theme.palette.primary.main
  const background = alpha(color, 0.12)
  return <RangeLineChart {...props} color={color} background={background} />
}

export default class OverviewRisk extends Component {
  static propTypes = {
    period: PERIOD.isRequired,
    riskTrends: PropTypes.arrayOf(RISK_TREND).isRequired,
    idioVsFactors: PropTypes.arrayOf(PropTypes.shape({
      date: DATE.isRequired,
      value: PropTypes.number.isRequired,
    })).isRequired,
    gmv: PropTypes.arrayOf(PropTypes.shape({
      date: DATE.isRequired,
      value: PropTypes.number.isRequired,
    })).isRequired,
    date: DATE,
    setPeriod: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    resetDate: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    endDate: DATE,
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    dailyRisk: PropTypes.object,
    gmvTitle: PropTypes.string,
    firstDate: PropTypes.string,
    categories: PropTypes.arrayOf(RISK_CATEGORY),
    datesAdjusted: PropTypes.bool.isRequired,
    currency: PropTypes.string.isRequired,
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, date } = this.props
    const startDate = firstDate || propsStartDate

    if (date && (date < startDate)) {
      this.props.resetDate()
    }
  }

  setPeriod = change => {
    if (change.startDate) {
      if (change.adjusted.startDate || change.adjusted.endDate) {
        this.props.addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${change.adjusted.startDate || change.startDate} to ${change.adjusted.endDate || change.endDate}.`)
      }

      this.props.setPeriod({
        startDate: change.adjusted.startDate || change.startDate,
        endDate: change.adjusted.endDate || change.endDate,
      })
    } else {
      this.props.setPeriod(change)
    }
  }

  render() {
    const {
      period,
      earliestDate,
      latestDate,
      date,
      setDate,
      endDate,
      firstDate,
      gmv,
      gmvTitle,
      idioVsFactors,
      riskTrends,
      dailyRisk,
      categories,
      startDate,
      resetDate,
      datesAdjusted,
      currency,
    } = this.props

    if (!riskTrends?.length) {
      return <NoData title="Risk Contribution" />
    }

    return <div className="modules_risk">
      <Card square className="header">
        <div className="label">Risk Decomposition</div>
        <PeriodSelector {...{ periodAdjusted: datesAdjusted, earliestDate, latestDate, endDate, startDate: firstDate || startDate, period, onChange: this.setPeriod }} />
        {date < endDate && <SelectedDate date={date} resetHandler={resetDate} />}
      </Card>
      <Card square className="content">
        <div className="top">
          <div className="gmv">
            <h4 className="header">{gmvTitle}</h4>
            <GmvRangeLineChart
              tooltip={<Tooltip id="gmv" gmvTitle={gmvTitle} riskTrends={riskTrends} gmv={gmv} currency={currency} />}
              {...{ startDate: firstDate || startDate, endDate, date, setDate, height: 200 }}
              data={gmv}
              formatter={makeCurrencyFormatter(currency)}
              id="gmv"
            />
          </div>
          <div className="idio-vs-factors">
            <h4 className="header">Factors vs. Specific</h4>
            <StackedLineChart
              tooltip={<Tooltip riskTrends={riskTrends} gmv={gmv} gmvTitle={gmvTitle} currency={currency} />}
              {...{
                startDate: firstDate || startDate,
                endDate,
                data: idioVsFactors,
                setDate,
                date,
              }}
            />
          </div>
        </div>
        <div className="history">
          <h5 className="title">Factor Trends</h5>
          {riskTrends.map(category => ([
            <h4 className="header" key={`${category.id}_header`} style={{ color: category.color }}>{category.label}</h4>,
            <RangeLineChart
              key={category.id}
              tooltip={<Tooltip id={category.id} gmv={gmv} riskTrends={riskTrends} gmvTitle={gmvTitle} currency={currency} />}
              {...{
                startDate: firstDate || startDate,
                endDate,
                date,
                setDate,
                height: 150,
              }}
              {...category}
            />,
          ]))}
        </div>
        <div className="details">
          {date ? <h5 className="title">{`Top Factor Contributors as of ${formatLongDate(date)}`}</h5> : <h5 className="title">No Data</h5>}
          {date && <RiskDetails date={date} categories={categories} dailyRisk={dailyRisk} />}
        </div>
      </Card>
    </div>
  }
}
